import React from 'react';
import { StyleSheet, TextStyle, View } from 'react-native';
import Button, {
  ButtonSize,
  ButtonType,
  ButtonWidth,
} from 'shared/components/vxl/Button/Button';
import Icon, { IconKeyType } from 'shared/components/vxl/icon/Icon/Icon';
import IconButton from 'shared/components/vxl/icon/IconButton/IconButton';
import BodySmBoldText from 'shared/components/vxl/typography/primary/body/BodySmBoldText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import theme from 'shared/styles/theme';

import {
  AlignmentStyle,
  ComposableStyle,
  SystemBannerType,
} from './SystemBanner.types';

interface BannerViewProps {
  /** It act's as a iconKey for Icon component. Leading icon is configurable for all the banner types, also we have default icons set based on the banner type.  */
  iconKeyType?: IconKeyType;
  /** Indicates main title of the banner */
  titleText?: string;
  /** Body text of the banner */
  bodyText?: string;
  /** Type of the banner e.g .info, .success etc */
  bannerType: SystemBannerType;
  /** Button prop accept Button component props as an argument. Use `Secondary` button type for SystemBanner*/
  button?: {
    label: string;
    onPress: () => void;
  };
  /** OnDismiss handler - Gets called when user dismiss the banner, non-nil onDismiss will show close icon on the banner */
  onDismiss?: () => void;
  /** OnCardPress handler - An optional handler gets called when user taps the whole card, non-nil onCardPress will show the chevron icon on the trailing side */
  onCardPress?: () => void;
  /** Styles for aligning the container and inner elements of the banner, based on the presence of the card press and button props. */
  alignmentStyles: AlignmentStyle;
  /** Computed styles for the banner based on its type and state (e.g., default, hover, press, focus). */
  computedStyles: ComposableStyle;
  /** Text color styles for the banner, determined by the banner type to ensure appropriate contrast and readability. */
  textColorStyles: TextStyle;
}

const getIconColor = (bannerType: SystemBannerType): string => {
  switch (bannerType) {
    case SystemBannerType.WARNING:
      return theme.color.text.primary;
    default:
      return theme.color.interactive.inverse;
  }
};

export const getLeadingIconKey = (
  bannerType: SystemBannerType,
): IconKeyType | undefined => {
  switch (bannerType) {
    case SystemBannerType.INFO:
      return 'INFO';
    case SystemBannerType.SUCCESS:
      return 'SUCCESS';
    case SystemBannerType.WARNING:
      return 'WARNING';
    case SystemBannerType.ERROR:
      return 'ERROR';
    case SystemBannerType.BRAND:
      return undefined;
  }
};

const getButtonProps = (bannerType: SystemBannerType) => {
  switch (bannerType) {
    case SystemBannerType.WARNING:
      return {
        type: 'SECONDARY_ALT' as ButtonType,
        size: 'SMALL' as ButtonSize,
        width: 'HUG' as ButtonWidth,
      };
    default:
      return {
        type: 'SECONDARY' as ButtonType,
        onDark: true,
        size: 'SMALL' as ButtonSize,
        width: 'HUG' as ButtonWidth,
      };
  }
};

const BannerView = ({
  iconKeyType,
  titleText,
  bodyText,
  bannerType,
  button,
  onDismiss,
  alignmentStyles,
  computedStyles,
  textColorStyles,
  onCardPress,
}: BannerViewProps) => {
  const buttonProps = getButtonProps(bannerType);

  return (
    <View
      style={[
        baseStyles.container,
        computedStyles.container,
        alignmentStyles.container,
      ]}
      role="alert"
      accessible
    >
      <View style={[baseStyles.innerContainer, alignmentStyles.innerContainer]}>
        {iconKeyType ? (
          <View style={baseStyles.leadingIcon}>
            <Icon
              iconColor={getIconColor(bannerType)}
              iconKey={iconKeyType}
              iconSize="MD"
            />
          </View>
        ) : (
          <View style={baseStyles.leadingIcon}>
            <Icon
              iconColor={getIconColor(bannerType)}
              iconKey={getLeadingIconKey(bannerType)}
              iconSize="MD"
            />
          </View>
        )}

        <View style={baseStyles.textContainer}>
          {titleText ? (
            <BodySmBoldText style={textColorStyles} testID="banner-title">
              {titleText}
            </BodySmBoldText>
          ) : null}
          {bodyText ? (
            <BodySmText style={textColorStyles} testID="banner-body">
              {bodyText}
            </BodySmText>
          ) : null}

          {button ? (
            <View style={baseStyles.button}>
              <Button {...button} {...buttonProps} />
            </View>
          ) : null}
        </View>
      </View>

      {onDismiss ? (
        <View style={baseStyles.trailingContainer} testID="banner-dismiss">
          <IconButton
            iconColor={getIconColor(bannerType)}
            iconKey="CLOSE"
            iconOnPress={onDismiss}
            iconButtonAccessibilityLabel="icon-dismiss"
          />
        </View>
      ) : null}

      {!onDismiss && !button && onCardPress ? (
        <View style={baseStyles.trailingContainer}>
          <Icon iconColor={getIconColor(bannerType)} iconKey="CHEVRON_RIGHT" />
        </View>
      ) : null}
    </View>
  );
};
const baseStyles = StyleSheet.create({
  button: {
    alignSelf: 'baseline',
    marginTop: theme.spacing.m,
  },
  container: {
    borderWidth: 1,
    flexDirection: 'row',
    height: 'auto',
    justifyContent: 'space-between',
    padding: theme.spacing.m,
    width: '100%',
  },
  innerContainer: {
    flexBasis: 100,
    flexDirection: 'row',
    flexGrow: 1,
  },
  leadingIcon: {
    marginRight: theme.spacing.m,
  },
  textContainer: {
    flexDirection: 'column',
    flexShrink: 1,
  },
  trailingContainer: {
    marginLeft: theme.spacing.m,
  },
});

export default BannerView;
